'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Button, Slider } from '../../components/ui';
import { Credits } from '../../components/Credits';
import { useBalance } from '../../queries/useProfileQueries';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { MAX_UPGRADE_ENTRY_SHARE } from '../../queries/useUpgradeQueries';
import { cn } from '../../utils/cn';
import translations from './UpgradeScreen.translations';
import type { UpgradeItem } from './UpgradeScreen';

interface UpgradeEntryCardProps {
  item: UpgradeItem | null;
  amount: number | null;
  targetPrice?: number | null;
  disabled?: boolean;
  onChange: (amount: number | null) => void;
  className?: string;
}

export const UpgradeEntryCard: React.FC<UpgradeEntryCardProps> = ({
  item,
  amount,
  targetPrice = null,
  disabled = false,
  onChange,
  className,
}) => {
  const { t } = useLocalTranslation(translations);
  const balance = useBalance();
  const [draft, setDraft] = useState(amount ?? 0);
  const commitRef = useRef(onChange);
  commitRef.current = onChange;

  useEffect(() => {
    setDraft(amount ?? 0);
  }, [amount]);

  const commit = (value: number) => {
    const next = value > 0 ? value : null;
    if (next === (amount ?? null)) return;
    commitRef.current(next);
  };

  const itemPrice = item?.price ?? 0;
  const entryCeiling = targetPrice ? Math.floor(targetPrice * MAX_UPGRADE_ENTRY_SHARE) : null;
  const creditsCeiling = entryCeiling === null ? null : Math.max(0, entryCeiling - itemPrice);
  const sliderMax = !item ? 0 : creditsCeiling === null ? balance : Math.min(balance, creditsCeiling);
  const total = itemPrice + draft;

  // WHY: сменилась цель или предмет — набранные кредиты поджимаем под потолок шанса; по балансу не режем,
  // иначе профиль, который ещё грузится, обнулил бы выбранное
  useEffect(() => {
    if (creditsCeiling === null || draft <= creditsCeiling) return;
    setDraft(creditsCeiling);
    commitRef.current(creditsCeiling > 0 ? creditsCeiling : null);
  }, [draft, creditsCeiling]);

  // WHY: обе строки всегда на месте и в одну линию — иначе блок менял высоту на каждое движение ползунка
  const header = (
    <>
      <p className="m-0 truncate text-[13px] text-label-secondary">{t('creditsAddLabel')}</p>
      <span
        className={cn(
          'flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[15px] font-bold tabular-nums',
          draft > 0 ? 'text-price' : 'text-white/25',
        )}
      >
        +<Credits amount={draft} iconSize={13} />
      </span>
    </>
  );

  const footerLine = (
    <div className="flex h-[16px] items-center justify-between gap-3 overflow-hidden whitespace-nowrap text-xs tabular-nums">
      <span className="text-white/35">
        {t('creditsBalance')} <Credits amount={balance} iconSize={10} className="text-white/45" />
      </span>
      {item ? (
        <span className="flex items-center gap-1.5 text-white/45">
          {t('entryTotalLabel')}
          <Credits amount={total} iconSize={11} className="font-bold text-white" />
        </span>
      ) : (
        <span className="text-white/30">{t('entryNeedsItem')}</span>
      )}
    </div>
  );

  const sliderNode = (
    <Slider
      value={draft}
      max={sliderMax}
      step={100}
      disabled={disabled || sliderMax <= 0}
      onChange={setDraft}
      onCommit={commit}
      aria-label={t('creditsAddLabel')}
      data-testid="upgrade-credits-slider"
    />
  );

  const toggleButton = (
    <Button
      variant="tertiary"
      size="s"
      disabled={disabled || sliderMax <= 0}
      className="shrink-0 border border-white/[0.08] bg-white/[0.04] text-white/70"
      onClick={() => {
        const next = draft > 0 ? 0 : sliderMax;
        setDraft(next);
        commit(next);
      }}
      data-testid={draft > 0 ? 'upgrade-credits-clear' : 'upgrade-credits-all'}
    >
      {draft > 0 ? t('creditsClear') : t('creditsAll')}
    </Button>
  );

  return (
    <section
      data-testid="upgrade-entry"
      className={cn('w-full overflow-hidden rounded-[21px] border border-white/[0.07] bg-bg-secondary', className)}
    >
      <div className="flex items-baseline justify-between gap-3 px-4 pt-2.5">{header}</div>

      <div className="flex items-center gap-2 px-4 pt-1.5">
        {sliderNode}
        {toggleButton}
      </div>

      <div className="px-4 pb-2.5 pt-1">{footerLine}</div>

    </section>
  );
};
